import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import moment from "moment";

import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";

//Components
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import Pagination from "../CustomUIComponents/Pagination/Pagination";

//Constants
import {
  SEARCH_FILTER_TYPE,
  SEARCH_FILTER_FIELDS,
  DOWNLOAD_FILE_TYPE,
  DATE_TYPE,
  DEFAULT_DURATION,
  DEFAULT_DATE_FORMAT,
  CONFIGURATION_DATE_TIME_FORMAT,
} from "../../constants/appConstants";
import {
  DEFAULT_PAGINATION_ACTIVE_PAGE,
  DEFAULT_PAGINATION_RECORDS_COUNT,
} from "../CustomUIComponents/Pagination/PaginationConstants";
import {
  COLLECTION_FILTER_ALL,
  COLLECTION_APPROVAL_STATUS,
  getStatusFilterOptions,
  getPaymentModeFilterOptions,
  getBankFilterOptions,
  getStatusLabel,
} from "./Constants";

//Assets
import CSVdownload from "../../resources/images/ic_download_csv.svg";
import PDFdownload from "../../resources/images/ic_download_pdf.svg";

//Strings
import { Strings } from "../../resources/i18n/i18n";

// Styles
import "../CustomUIComponents/CustomTableCard/CustomTableCard.scss";
import "./CollectionApproval.scss";

//Utils
import Utility from "../../utils/Utility";

// the receipt picture scaled to fit its box; the frame around it then has the picture's exact size
const FittedImage = ({ src, alt, className, fit }) => {
  const imageRef = useRef(null);
  const [size, setSize] = useState(null);

  const measure = useCallback(() => {
    const image = imageRef.current;
    const box = image && image.closest(".evidenceContainer");
    if (!fit || !box || !image.naturalWidth || !image.naturalHeight) {
      return;
    }
    const scale = Math.min(
      box.clientWidth / image.naturalWidth,
      box.clientHeight / image.naturalHeight
    );
    setSize({
      width: Math.round(image.naturalWidth * scale),
      height: Math.round(image.naturalHeight * scale),
    });
  }, [fit]);

  useEffect(() => {
    const box =
      imageRef.current &&
      imageRef.current.closest(".evidenceContainer");
    if (!fit || !box || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [fit, measure]);

  return (
    <img
      ref={imageRef}
      src={src}
      alt={alt}
      className={className}
      style={
        size ? { width: size.width, height: size.height } : undefined
      }
      onLoad={measure}
    />
  );
};

class CollectionApproval extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      previewModal: false,
      decisionModal: false,
      // null until the approver picks Approve or Reject inside the dialog
      isApproval: null,
      selectedRequest: null,
      waiveLateFee: false,
      note: "",
      noteError: "",
      bankId: "",
      bankError: "",
      filters: this.getDefaultFilters(),
      activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
      recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
    };
  }

  // the screen opens on what still needs a decision, over the usual last week
  getDefaultFilters = () => ({
    fromDate: DEFAULT_DURATION.fromDate,
    toDate: moment(new Date()).format(DEFAULT_DATE_FORMAT),
    minimartId: "",
    paymentMode: COLLECTION_FILTER_ALL,
    bankId: COLLECTION_FILTER_ALL,
    status: COLLECTION_APPROVAL_STATUS.PENDING_APPROVAL,
  });

  componentDidMount() {
    this.handleOnClickSearchFilter(true);
  }

  // "All" is a UI value only, the server takes the filter out when it is absent
  withoutAll = (value) =>
    value && value !== COLLECTION_FILTER_ALL ? value : undefined;

  // midday of the picked day, so a time zone difference cannot move it to another day; the server widens it to the whole day
  noonOf = (date) =>
    moment(date).startOf("day").add(12, "hours").valueOf();

  buildSearch = () => {
    const {
      fromDate,
      toDate,
      minimartId,
      paymentMode,
      bankId,
      status,
    } = this.state.filters;
    return {
      fd: fromDate ? this.noonOf(fromDate) : undefined,
      td: toDate ? this.noonOf(toDate) : undefined,
      cd: minimartId ? Number(minimartId) : undefined,
      pm: this.withoutAll(paymentMode),
      bk: this.withoutAll(bankId) ? Number(bankId) : undefined,
      st: this.withoutAll(status),
    };
  };

  handleOnClickSearchFilter = (isDefaultOffset = false) => {
    isDefaultOffset === true &&
      this.setState({ activePage: DEFAULT_PAGINATION_ACTIVE_PAGE });
    this.props.onSearch({
      search: this.buildSearch(),
      offset:
        isDefaultOffset === true
          ? DEFAULT_PAGINATION_ACTIVE_PAGE - 1
          : this.state.activePage - 1,
      limit: this.state.recordsPerPage,
    });
  };

  handleOnClickResetFilter = () => {
    this.setState(
      {
        filters: this.getDefaultFilters(),
        activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
        recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
      },
      this.handleOnClickSearchFilter
    );
  };

  onPageChange = ({ page, pageLength }) => {
    this.setState(
      { activePage: page, recordsPerPage: pageLength },
      this.handleOnClickSearchFilter
    );
  };

  handleInputChange = (event) => {
    this.setState({
      filters: {
        ...this.state.filters,
        [event.target.name]: event.target.value,
      },
    });
  };

  handleDateChange = (key) => (dateMoment) => {
    let dateValue = "";
    if (key === DATE_TYPE.FROM_DATE) {
      if (dateMoment.isAfter(this.state.filters.toDate)) {
        dateValue = this.state.filters.fromDate;
      } else {
        dateMoment.set({ hour: 0, minute: 0, second: 0 });
        dateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
      }
    } else if (key === DATE_TYPE.TO_DATE) {
      if (dateMoment.isBefore(this.state.filters.fromDate)) {
        dateValue = this.state.filters.toDate;
      } else {
        dateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
      }
    }
    this.setState({
      filters: { ...this.state.filters, [key]: dateValue },
    });
  };

  renderSearchFilter = () => {
    const { filters } = this.state;
    const searchFilterList = [
      {
        type: SEARCH_FILTER_TYPE.DATE,
        handleCalenderChange: this.handleDateChange,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fieldname: SEARCH_FILTER_FIELDS.DATE,
        column: 3,
      },
      {
        type: SEARCH_FILTER_TYPE.INPUT,
        searchPlaceHolder: Strings("collectionFilterMinimartId"),
        Value: filters.minimartId,
        handleOnChange: this.handleInputChange,
        inputType: SEARCH_FILTER_TYPE.NUMBER,
        fieldname: "minimartId",
        name: "minimartId",
        column: 1,
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        handleOnChange: this.handleInputChange,
        Value: filters.status,
        fieldname: "status",
        name: "status",
        filterDropdownList: getStatusFilterOptions(),
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        handleOnChange: this.handleInputChange,
        Value: filters.paymentMode,
        fieldname: "paymentMode",
        name: "paymentMode",
        filterDropdownList: getPaymentModeFilterOptions(),
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        handleOnChange: this.handleInputChange,
        Value: filters.bankId,
        fieldname: "bankId",
        name: "bankId",
        filterDropdownList: getBankFilterOptions(this.props.banks),
      },
    ];
    return (
      <SearchFilter
        filters={searchFilterList}
        handleOnClickSearchFilter={() =>
          this.handleOnClickSearchFilter(true)
        }
        handleOnClickResetFilter={this.handleOnClickResetFilter}
        paddingLeft={2}
      />
    );
  };

  downloadFile = (fileType) => {
    this.props.onDownload(fileType, { search: this.buildSearch() });
  };

  renderDownloadButtons = () => (
    <div className='text-end pe-3 flex-shrink-0 downloadPanel'>
      <Button
        className='downloadButtons me-3'
        onClick={() => this.downloadFile(DOWNLOAD_FILE_TYPE.PDF)}
        disabled={
          !this.props.requests || this.props.requests.length === 0
        }
      >
        <img src={PDFdownload} alt='' />
      </Button>
      <Button
        className='downloadButtons'
        onClick={() => this.downloadFile(DOWNLOAD_FILE_TYPE.CSV)}
        disabled={
          !this.props.requests || this.props.requests.length === 0
        }
      >
        <img src={CSVdownload} alt='' />
      </Button>
    </div>
  );

  openDecision = (request) => {
    if (request?.evidenceId) {
      this.props.onViewEvidence(request.evidenceId);
    }
    this.setState({
      decisionModal: true,
      isApproval: null,
      selectedRequest: request,
      waiveLateFee: false,
      note: "",
      noteError: "",
      bankId: "",
      bankError: "",
    });
  };

  closeDecision = () => {
    this.setState({ decisionModal: false, selectedRequest: null });
    this.props.onClearEvidence();
  };

  // the receipt can be looked at straight from the list, without opening a review
  openPreview = (request) => {
    this.props.onViewEvidence(request.evidenceId);
    this.setState({ previewModal: true });
  };

  closePreview = () => {
    this.setState({ previewModal: false });
    this.props.onClearEvidence();
  };

  // approve and reject are separate buttons, the waiver and the note are always on screen
  submitDecision = (isApproval) => {
    const { selectedRequest, waiveLateFee, note, bankId } =
      this.state;
    // a rejection must say why, and so must an approval
    if (note.trim() === "") {
      this.setState({
        noteError: Strings(
          isApproval ? "approvalNoteMandatory" : "rejectionNoteMandatory"
        ),
      });
      return;
    }
    // a request raised without a bank cannot be approved until the approver names it
    if (isApproval && !selectedRequest.bankId && bankId === "") {
      this.setState({ bankError: Strings("selectBankToApprove") });
      return;
    }
    this.closeDecision();
    this.props.onDecision(
      selectedRequest.approvalId,
      isApproval,
      waiveLateFee,
      note.trim(),
      bankId === "" ? undefined : Number(bankId)
    );
  };

  // fit: the receipt is scaled to the box it is in, so the frame (and the icon on its corner) matches the picture
  renderEvidence = (fit = false) => {
    const { evidenceUrl, evidenceFileType, evidenceLoading } =
      this.props;
    if (evidenceLoading) {
      return <span className='label'>{Strings("Loading")}</span>;
    }
    if (!evidenceUrl) {
      return (
        <span className='label'>{Strings("NoRecordsFound")}</span>
      );
    }
    const isPdf =
      evidenceFileType &&
      evidenceFileType.toLowerCase().includes("pdf");
    // the frame is as big as the receipt itself, so the icon sits on the receipt's own corner
    return (
      <div className='evidenceViewer'>
        <div
          className={`evidenceFrame${isPdf ? " evidencePdf" : ""}`}
        >
          {isPdf ? (
            <object
              data={evidenceUrl}
              type='application/pdf'
              className='evidenceDocument'
              aria-label={Strings("viewEvidence")}
            />
          ) : (
            <FittedImage
              src={evidenceUrl}
              alt={Strings("viewEvidence")}
              className='evidenceImage'
              fit={fit}
            />
          )}
          {/* the receipt is downloaded from the corner, a click on it does nothing */}
          <button
            type='button'
            className='evidenceDownload'
            title={Strings("downloadEvidence")}
            aria-label={Strings("downloadEvidence")}
            onClick={this.downloadEvidence}
          >
            <DownloadIcon style={{ fontSize: "0.75rem" }} />
          </button>
        </div>
      </div>
    );
  };

  // the file lives on another domain, so it is fetched and saved as a blob; if the
  // browser refuses that, the receipt opens in a new tab where it can be saved
  downloadEvidence = async () => {
    const { evidenceUrl, evidenceFileType } = this.props;
    const type = (evidenceFileType || "").toLowerCase();
    const extension = type.includes("pdf")
      ? "pdf"
      : type.replace(/^.*\//, "").replace("jpeg", "jpg") || "jpg";
    try {
      const response = await fetch(evidenceUrl);
      if (!response.ok) {
        throw new Error(response.status);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `payment-receipt.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(evidenceUrl, "_blank");
    }
  };

  renderDecisionModal = () => {
    const {
      selectedRequest,
      waiveLateFee,
      note,
      noteError,
      bankId,
      bankError,
    } = this.state;
    const accrued =
      selectedRequest?.accruedLateFeeSinceSubmission ?? 0;
    const hasEvidence = !!selectedRequest?.evidenceId;
    return (
      <div className='collectionApprovalModal'>
        <Row>
          {/* the details and the decision on the left, the slip to check them against on the right */}
          <Col md={hasEvidence ? 7 : 12}>
            <Row>
              <Col md={4}>
                <span className='label'>
                  {Strings("collectionFilterMinimartId")}
                </span>
                <span className='value'>
                  {selectedRequest?.minimartId}
                </span>
              </Col>
              <Col md={4}>
                <span className='label'>{Strings("Minimart")}</span>
                <span className='value'>
                  {selectedRequest?.minimartName}
                </span>
              </Col>
              <Col md={4}>
                <span className='label'>
                  {Strings("paymentLoan")}
                </span>
                <span className='value strong'>
                  {selectedRequest?.collectionType === "DOWNPAYMENT"
                    ? `${Strings("downPaymentRequest")} ${selectedRequest?.applicationId ?? ""}`
                    : selectedRequest?.loanId || Strings("allLoans")}
                </span>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <span className='label'>{Strings("Amount")}</span>
                <span className='value strong'>
                  {Utility.getCurrencyRepresentationOfAmount(
                    selectedRequest?.amount
                  )}
                </span>
              </Col>
              <Col md={6}>
                <span className='label'>
                  {Strings("paymentMode")}
                </span>
                <span className='value'>
                  {selectedRequest?.paymentMode
                    ? Strings(selectedRequest.paymentMode)
                    : Strings("NA")}
                </span>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <span className='label'>
                  {Strings("destinationBank")}
                </span>
                <span className='value'>
                  {selectedRequest?.bankName || Strings("NA")}
                </span>
              </Col>
              <Col md={6}>
                <span className='label'>{Strings("raisedBy")}</span>
                <span className='value'>
                  {selectedRequest?.collectorName}
                </span>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <span className='label'>{Strings("expiresOn")}</span>
                <span className='value'>
                  {selectedRequest?.expiresAt
                    ? moment(selectedRequest.expiresAt).format(
                        CONFIGURATION_DATE_TIME_FORMAT
                      )
                    : Strings("NA")}
                </span>
              </Col>
              {selectedRequest?.collectorNote && (
                <Col md={6}>
                  <span className='label'>{Strings("Note")}</span>
                  <span className='value'>
                    {selectedRequest.collectorNote}
                  </span>
                </Col>
              )}
            </Row>

            {/* a decided request is only shown, with the decision and its note in place of the controls */}
            {selectedRequest?.status !==
            COLLECTION_APPROVAL_STATUS.PENDING_APPROVAL ? (
              this.renderDecisionDetails()
            ) : (
            <>
            {/* an older app sends no bank, so the approver records where the money went */}
            {selectedRequest && !selectedRequest.bankId && (
              <Row>
                <Col md={12}>
                  <span className='label'>
                    {Strings("destinationBank")}
                  </span>
                  <Form.Select
                    value={bankId}
                    onChange={(e) =>
                      this.setState({
                        bankId: e.target.value,
                        bankError: "",
                      })
                    }
                  >
                    <option value=''>
                      {Strings("selectBankToApprove")}
                    </option>
                    {(this.props.banks || []).map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.name}
                      </option>
                    ))}
                  </Form.Select>
                  {bankError !== "" && (
                    <span className='errorText'>{bankError}</span>
                  )}
                </Col>
              </Row>
            )}

            {/* always on screen, the figure shows what the delay cost so far, nothing is waived when it is zero */}
            {selectedRequest && (
              <Row>
                <Col md={12}>
                  <Form.Check
                    type='checkbox'
                    id='waiveLateFee'
                    checked={waiveLateFee}
                    onChange={(e) =>
                      this.setState({
                        waiveLateFee: e.target.checked,
                      })
                    }
                    label={`${Strings(
                      "waiveLateFeeQuestion"
                    )} (${Utility.getCurrencyRepresentationOfAmount(accrued)})`}
                  />
                </Col>
              </Row>
            )}

            {/* always on screen, required for both a rejection and an approval */}
            <Row>
              <Col md={12}>
                <span className='label'>{`${Strings("Note")} *`}</span>
                <textarea
                  className='noteInput'
                  rows={3}
                  value={note}
                  onChange={(e) =>
                    this.setState({
                      note: e.target.value,
                      noteError: "",
                    })
                  }
                />
                {noteError !== "" && (
                  <span className='errorText'>{noteError}</span>
                )}
              </Col>
            </Row>

            {/* cancel, approve and reject, directly under the note */}
            <div className='modalButton decisionButtons'>
              <div className='pe-1'>
                <Button
                  className='primaryModalButton buttonMinWidth buttonBorder'
                  onClick={this.closeDecision}
                >
                  {Strings("Cancel")}
                </Button>
              </div>
              <div className='ps-1 pe-1'>
                <Button
                  className='primaryModalButton buttonMinWidth buttonBackground'
                  onClick={() => this.submitDecision(true)}
                >
                  {Strings("approvePayment")}
                </Button>
              </div>
              <div className='ps-1'>
                <Button
                  className='primaryModalButton buttonMinWidth rejectButtonBackground'
                  onClick={() => this.submitDecision(false)}
                >
                  {Strings("rejectPayment")}
                </Button>
              </div>
            </div>
            </>
            )}
          </Col>
          {/* the approver checks the slip against online banking */}
          {hasEvidence && (
            <Col md={5} className='evidenceColumn'>
              <span className='label'>{Strings("viewEvidence")}</span>
              <div className='evidenceContainer'>
                {this.renderEvidence(true)}
              </div>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  // UserModal only takes fixed text and buttons, so the review dialog uses the same modal
  // library and the app's own modal styles for a custom body
  renderDecisionDialog = () => {
    const { decisionModal } = this.state;
    return (
      <Modal
        open={decisionModal}
        onClose={this.closeDecision}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        showCloseIcon={false}
        classNames={{ modal: "customModal collectionReviewModal" }}
        center
      >
        <div className='userModal'>
          <div className='headerModal'>
            {this.state.selectedRequest?.status ===
            COLLECTION_APPROVAL_STATUS.PENDING_APPROVAL
              ? Strings("reviewPayment")
              : Strings("paymentRequestDetails")}
          </div>
          <div className='modalContent'>
            {this.renderDecisionModal()}
          </div>
        </div>
      </Modal>
    );
  };

  renderPreviewDialog = () => (
    <Modal
      open={this.state.previewModal}
      onClose={this.closePreview}
      showCloseIcon={false}
      classNames={{ modal: "customModal collectionPreviewModal" }}
      center
    >
      <div className='userModal'>
        <div className='headerModal'>
          {Strings("uploadedReceipt")}
        </div>
        <div className='modalContent'>
          <div className='collectionApprovalModal'>
            <div className='evidenceContainer'>
              {this.renderEvidence()}
            </div>
          </div>
          <div className='modalButton'>
            <Button
              className='primaryModalButton buttonMinWidth buttonBorder'
              onClick={this.closePreview}
            >
              {Strings("Close")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );

  // key picks the value, md fixes a narrow column; the rest share what is left equally
  getColumns = () => [
    { key: "minimartId", name: "collectionFilterMinimartId", md: 1 },
    { key: "minimartName", name: "Minimart" },
    { key: "loanId", name: "paymentLoan" },
    { key: "amount", name: "Amount", md: 1 },
    { key: "paymentMode", name: "paymentMode" },
    { key: "bankName", name: "destinationBank" },
    { key: "evidence", name: "uploadedReceipt" },
    { key: "collectorName", name: "raisedBy" },
    { key: "createdAt", name: "raisedOn" },
    { key: "expiresAt", name: "expiresOn" },
    { key: "status", name: "paymentRequestStatus" },
    { key: "action", name: "Action", md: 2 },
  ];

  renderValue = (request, key) => {
    switch (key) {
      case "amount":
        return Utility.getCurrencyRepresentationOfAmount(
          request.amount
        );
      case "minimartId":
        return (
          <Button
            variant='link'
            className='receiptLink p-0'
            onClick={() =>
              this.props.onOpenMinimart(request.minimartId)
            }
          >
            {request.minimartId}
          </Button>
        );
      case "loanId":
        // a down payment is for a loan application that has no loan yet
        if (request.collectionType === "DOWNPAYMENT") {
          return request.applicationId ? (
            <Button
              variant='link'
              className='receiptLink p-0'
              onClick={() => this.props.onOpenLoan(request)}
            >
              {`${Strings("downPaymentRequest")} ${request.applicationId}`}
            </Button>
          ) : (
            Strings("downPaymentRequest")
          );
        }
        // no loan on the request means it was raised for the minimart as a whole
        if (!request.loanId) {
          return Strings("allLoans");
        }
        // the loan page opens by application id, a row without one has no page to go to
        return request.applicationId ? (
          <Button
            variant='link'
            className='receiptLink p-0'
            onClick={() => this.props.onOpenLoan(request)}
          >
            {request.applicationId}
          </Button>
        ) : (
          request.loanId
        );
      case "paymentMode":
        return request.paymentMode
          ? Strings(request.paymentMode)
          : Strings("NA");
      case "createdAt":
      case "expiresAt":
        return request[key]
          ? moment(request[key]).format(
              CONFIGURATION_DATE_TIME_FORMAT
            )
          : Strings("NA");
      case "status":
        return getStatusLabel(request.status);
      case "evidence":
        // a request raised without a slip has nothing to show
        return request.evidenceId ? (
          <Button
            variant='link'
            className='receiptLink p-0'
            onClick={() => this.openPreview(request)}
          >
            {Strings("viewEvidence")}
          </Button>
        ) : (
          Strings("NA")
        );
      default:
        return request[key];
    }
  };

  renderDecisionButtons = (request) =>
    // only a request still waiting can be decided, a decided one opens to be read
    request.status ===
    COLLECTION_APPROVAL_STATUS.PENDING_APPROVAL ? (
      <div className='decisionButtons'>
        <Button
          size='sm'
          className='reviewButton'
          title={Strings("reviewPayment")}
          onClick={() => this.openDecision(request)}
        >
          {Strings("reviewAction")}
        </Button>
      </div>
    ) : (
      <div className='decisionButtons'>
        <Button
          size='sm'
          className='reviewButton'
          title={Strings("paymentRequestDetails")}
          onClick={() => this.openDecision(request)}
        >
          {Strings("View")}
        </Button>
      </div>
    );

  renderHeaderRow = () => (
    <Card className='mt-3 headerRow py-2'>
      <CardActions disableSpacing>
        {this.getColumns().map((column) => (
          <Col
            md={column.md}
            key={column.key}
            className='text-center'
          >
            <Typography className='headerContent'>
              {Strings(column.name)}
            </Typography>
          </Col>
        ))}
      </CardActions>
    </Card>
  );

  renderRow = (request) => (
    <Card key={request.approvalId} className='tableRow py-1 allRows'>
      <CardActions>
        {this.getColumns().map((column) => (
          <Col
            md={column.md}
            key={column.key}
            className='text-center'
          >
            {column.key === "action" ? (
              this.renderDecisionButtons(request)
            ) : (
              <Typography className='content'>
                {this.renderValue(request, column.key)}
              </Typography>
            )}
          </Col>
        ))}
      </CardActions>
    </Card>
  );

  render() {
    const { requests } = this.props;
    return (
      <div className='collectionApproval'>
        <div className='heading'>
          {Strings("collectionApprovalQueue")}
        </div>

        <div className='paper customTableCard'>
          <div className='d-flex align-items-start'>
            <div className='flex-grow-1'>
              {this.renderSearchFilter()}
            </div>
            {this.renderDownloadButtons()}
          </div>
          {requests && requests.length > 0 ? (
            <div className='mx-5 requestsBody'>
              {this.renderHeaderRow()}
              {requests.map((request) => this.renderRow(request))}
              <Row className='pt-3 mt-auto'>
                <Col md={12}>
                  <Pagination
                    activePage={this.state.activePage}
                    recordsPerPage={this.state.recordsPerPage}
                    numberOfPages={this.props.numberOfPages}
                    onPageChange={this.onPageChange}
                    dropup={true}
                  />
                </Col>
              </Row>
            </div>
          ) : (
            <div className='noRecordsFound'>
              {Strings("NoRecordsFound")}
            </div>
          )}
        </div>

        {this.renderDecisionDialog()}
        {this.renderPreviewDialog()}
      </div>
    );
  }

  // a decided request shows who decided it, when, and the note left with the approval or rejection
  renderDecisionDetails = () => {
    const { selectedRequest } = this.state;
    return (
      <>
        <Row>
          <Col md={6}>
            <span className='label'>
              {Strings("paymentRequestStatus")}
            </span>
            <span className='value strong'>
              {getStatusLabel(selectedRequest?.status)}
            </span>
          </Col>
          <Col md={6}>
            <span className='label'>{Strings("decidedBy")}</span>
            <span className='value'>
              {selectedRequest?.approverName || Strings("NA")}
            </span>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <span className='label'>{Strings("decidedOn")}</span>
            <span className='value'>
              {selectedRequest?.approvedAt
                ? moment(selectedRequest.approvedAt).format(
                    CONFIGURATION_DATE_TIME_FORMAT
                  )
                : Strings("NA")}
            </span>
          </Col>
          {selectedRequest?.lateFeeWaived && (
            <Col md={6}>
              <span className='label'>
                {Strings("lateFeesWaived")}
              </span>
              <span className='value'>
                {Utility.getCurrencyRepresentationOfAmount(
                  selectedRequest.lateFeeWaivedAmount ?? 0
                )}
              </span>
            </Col>
          )}
        </Row>
        <Row>
          <Col md={12}>
            <span className='label'>{Strings("decisionNoteLabel")}</span>
            <span className='value decisionNote'>
              {selectedRequest?.approvalNote || Strings("NA")}
            </span>
          </Col>
        </Row>
        <div className='modalButton decisionButtons'>
          <Button
            className='primaryModalButton buttonMinWidth buttonBorder'
            onClick={this.closeDecision}
          >
            {Strings("Close")}
          </Button>
        </div>
      </>
    );
  };
}

export default CollectionApproval;
